import { Component, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AlertController, IonNav } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { AccountDetailsPage } from '../account-details/account-details.page';
import { PaymentService } from 'src/app/services/payment.service';
import { ContactFormPage } from '../contact-form/contact-form.page';
import { PaymentOptionPage } from '../payment-plan/payment-option/payment-option.page';
import { PaymentFrequencyPage } from '../payment-plan/payment-frequency/payment-frequency.page';
import { PaymentDatePage } from '../payment-plan/payment-date/payment-date.page';
import { CustomPayOptionsPage } from '../payment-plan/custom-pay-options/custom-pay-options.page';
import { PaymentMethodPage } from '../payment-plan/payment-method/payment-method.page';
import { PaymentGatewaysPage } from '../payment-plan/payment-gateways/payment-gateways.page';
import { BankPaymentPage } from '../payment-plan/bank-payment/bank-payment.page';
import { MobipaidPage } from '../payment-gateway/non-recurring/mobipaid/mobipaid.page';
import { USAEPayMethodPage } from '../payment-gateway/non-recurring/usaepay/usaepay-method/usaepay-method.page';
import { PayRocPage } from '../payment-gateway/non-recurring/payroc/payroc.page';
import { StripepayPage } from '../payment-gateway/non-recurring/stripepay/stripepay.page';
import { GoCardlessPage } from '../payment-gateway/non-recurring/gocardless/gocardless.page';
import { AcquiredPage } from '../payment-gateway/non-recurring/acquired/acquired.page';
import { TrxServicesMethodPage } from '../payment-gateway/non-recurring/trxservices/trxservices-method/trxservices-method.page';
import { USAEPayAchPage } from '../payment-gateway/non-recurring/usaepay/usaepay-ach/usaepay-ach.page';
import { USAepayCardPage } from '../payment-gateway/non-recurring/usaepay/usaepay-card/usaepay-card.page';
import { TrxServicesAchPage } from '../payment-gateway/non-recurring/trxservices/trxservices-ach/trxservices-ach.page';
import { TrxServicesCardPage } from '../payment-gateway/non-recurring/trxservices/trxservices-card/trxservices-card.page';
import { NmiPage } from '../payment-gateway/non-recurring/nmi/nmi.page';
import { TrustPaymentMethodPage } from '../payment-gateway/non-recurring/trust-payment/trust-payment-method/trust-payment-method.page';
import { TrustPaymentPage } from '../payment-gateway/non-recurring/trust-payment/trust-payment.page';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'master-home',
  templateUrl: './master-home.page.html',
  styleUrls: ['./master-home.page.scss'],
})
export class MasterHomePage implements OnInit, OnDestroy {
  @ViewChild('myNav', { static: true }) myNav: IonNav;
  canShowContactUs = true;

  rootPage = AccountDetailsPage;

  private inactivityTimer: any;
  private inactivityMs = 300000;
  private isPopupOpen = false;

  constructor(
    private caseService: CaseService,
    private paymentService: PaymentService,
    private alertController: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.myNav.ionNavDidChange.subscribe(async () => {
      const activeComponentInstance = await this.myNav.getActive();
      this.wrapFooterAndSiblings();
      if (activeComponentInstance.component == ContactFormPage) {
        this.canShowContactUs = false;
      } else {
        this.canShowContactUs = true;
      }
      this.resetInactivityTimer().catch(console.error);
    });
    this.caseService.setMyNav(this.myNav);
    this.paymentService.onInit();
    this.resetInactivityTimer().catch(console.error);
  }

  ngOnDestroy() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
  }

  async showContactUs() {
    const stack = await this.myNav.getActive();
    if (stack.component != ContactFormPage)
      this.myNav.push(ContactFormPage, {
        caseId: this.caseService.firstCaseId,
      });
  }

  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  @HostListener('window:touchstart')
  onUserActivity() {
    this.resetInactivityTimer().catch(console.error);
  }

  private async resetInactivityTimer() {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    const timeoutMs = await this.getInactivityTimeout();
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivity();
    }, timeoutMs);
  }

  private async getInactivityTimeout(): Promise<number> {
    const settingsRaw = localStorage.getItem('portal_settings');
    if (!settingsRaw) {
      return this.inactivityMs;
    }

    try {
      const settings = JSON.parse(settingsRaw);
      const config = settings?.additional_settings?.inactivity_popup;

      const currentContext = await this.getCurrentContext();
      if (currentContext && config?.messages?.[currentContext]?.timeout_duration) {
        const instanceTimeout = config.messages[currentContext].timeout_duration;
        if (instanceTimeout > 0) {
          return instanceTimeout * 60000; // Convert minutes to milliseconds
        }
      }

      // Fall back to global timeout
      const timeoutMs = config?.timeout_duration_minutes * 60000 || this.inactivityMs;
      if (timeoutMs && timeoutMs > 0) {
        return timeoutMs;
      }
    } catch (_) {
    }

    return this.inactivityMs;
  }

  private async handleInactivity() {
    let settings: any;

    if (this.isPopupOpen) {
      return;
    }

    const settingsRaw = localStorage.getItem('portal_settings');
    if (!settingsRaw) {
      return;
    }
    try {
      settings = JSON.parse(settingsRaw);
    } catch (_) {
      return;
    }
    const config = settings?.additional_settings?.inactivity_popup;
    const whenToShow = config?.when_to_show || [];

    const shouldShow = await this.shouldShowForCurrentContext(whenToShow);
    if (!shouldShow) {
      this.resetInactivityTimer().catch(console.error);
      return;
    }

    const currentContext = await this.getCurrentContext();
    const instanceConfig = this.getInstanceConfig(config, currentContext);

    const message = instanceConfig.message || config?.inactivity_popup_message || '';
    const title = instanceConfig.title || config?.inactivity_popup_title || this.translate.instant('COMMON.NOTICE');

    if (!message) {
      return;
    }

    this.isPopupOpen = true;
    const alert = await this.alertController.create({
      header: title,
      message: `<div style="overflow-y:auto; max-height:60vh;">${message}</div>`,
      buttons: [
        {
          text: this.translate.instant('COMMON.OK'),
          handler: () => {
            this.isPopupOpen = false;
            this.resetInactivityTimer().catch(console.error);
          },
        },
      ],
    });
    await alert.present();
  }

  private async shouldShowForCurrentContext(whenToShow: string[]): Promise<boolean> {
    if (!whenToShow || whenToShow.length === 0 ||
        whenToShow.includes('all_pages') || whenToShow.includes('all')) {
      return true;
    }

    const active = await this.myNav.getActive();
    const cmp = active?.component;
    if (!cmp) {
      return false;
    }

    for (const context of whenToShow) {
      const normalizedContext = String(context).toLowerCase();

      if (normalizedContext === 'account_listing' || normalizedContext === 'account' || normalizedContext === 'listing') {
        if (cmp === AccountDetailsPage) {
          return true;
        }
      }

      if (normalizedContext === 'arrangement_process' || normalizedContext === 'arrangement') {
        const arrangementComponents: any[] = [
          PaymentOptionPage,
          PaymentFrequencyPage,
          PaymentDatePage,
          CustomPayOptionsPage,
          PaymentMethodPage,
        ];
        if (arrangementComponents.includes(cmp)) {
          return true;
        }
      }

      if (normalizedContext === 'payment_process' || normalizedContext === 'payment') {
        const paymentComponents: any[] = [
          PaymentGatewaysPage,
          BankPaymentPage,
          MobipaidPage,
          USAEPayMethodPage,
          USAEPayAchPage,
          USAepayCardPage,
          PayRocPage,
          StripepayPage,
          GoCardlessPage,
          AcquiredPage,
          TrxServicesMethodPage,
          TrxServicesAchPage,
          TrxServicesCardPage,
          NmiPage,
          TrustPaymentMethodPage,
          TrustPaymentPage,
        ];
        if (paymentComponents.includes(cmp)) {
          return true;
        }
      }
    }

    return false;
  }

  private async getCurrentContext(): Promise<string> {
    const active = await this.myNav.getActive();
    const cmp = active?.component;
    if (!cmp) {
      return '';
    }

    if (cmp === AccountDetailsPage) {
      return 'account_listing';
    }

    const arrangementComponents: any[] = [
      PaymentOptionPage,
      PaymentFrequencyPage,
      PaymentDatePage,
      CustomPayOptionsPage,
      PaymentMethodPage,
    ];
    if (arrangementComponents.includes(cmp)) {
      return 'arrangement_process';
    }

    const paymentComponents: any[] = [
      PaymentGatewaysPage,
      BankPaymentPage,
      MobipaidPage,
      USAEPayMethodPage,
      USAEPayAchPage,
      USAepayCardPage,
      PayRocPage,
      StripepayPage,
      GoCardlessPage,
      AcquiredPage,
      TrxServicesMethodPage,
      TrxServicesAchPage,
      TrxServicesCardPage,
      NmiPage,
      TrustPaymentMethodPage,
      TrustPaymentPage,
    ];
    if (paymentComponents.includes(cmp)) {
      return 'payment_process';
    }

    return '';
  }

  private getInstanceConfig(config: any, context: string): any {
    if (!config?.messages || !context) {
      return {};
    }

    const instanceMessages = config.messages[context];
    if (!instanceMessages) {
      return {};
    }

    return {
      title: instanceMessages.title || '',
      message: instanceMessages.message || '',
      timeout: instanceMessages.timeout_duration || null
    };
  }

  private wrapFooterAndSiblings() {
    const footers = document.querySelectorAll('master-footer');
    footers.forEach(footer => {
      const parent = footer.parentElement;
      if (!parent) return;
      if (parent.classList.contains('page-container')) return;

      const wrapper = document.createElement('div');
      wrapper.classList.add('page-container');

      const siblings = Array.from(parent.children);
      siblings.forEach(child => wrapper.appendChild(child));

      parent.appendChild(wrapper);
    });
  }

  closeHelpPopup() {
    this.canShowContactUs = false;
  }
}
